import { TPartnerResDto } from "@/api/services/main/partner/dtos/form-partner-req.dto";
import { TGetPartnerListResDto } from "@/api/services/main/partner/dtos/get-partner-list.dto";
import { TGetPartnerResDto } from "@/api/services/main/partner/dtos/get-partner.dto";
import { TPartnerReqDto, TUpdatePartnerResDto } from "@/api/services/main/partner/dtos/form-partner-req.dto";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { truncateWithEllipsis } from "@/utils/string";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

export const fetchPartnerListApi = async (): Promise<TGetPartnerListResDto> => {
  const rs = await clientApi.get<TGetPartnerListResDto>("/partners");
  return rs.data;
};

export const useGetPartnerList = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_LIST],
    queryFn: () => fetchPartnerListApi(),
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  const partnerOptions = useMemo(
    () => (data ? data.data.map((item) => ({ label: truncateWithEllipsis(item.partnerName ?? "", 50), value: item.id })) : []),
    [data],
  );

  return { data: data?.data ?? [], partnerOptions, ...rest };
};

export const createPartnerApi = async (payload: TPartnerReqDto): Promise<TPartnerResDto> => {
  const rs = await clientApi.post<TPartnerResDto>("/partners", payload);
  return rs.data;
};

export const updatePartnerApi = async (id: string | number, payload: TPartnerReqDto): Promise<TUpdatePartnerResDto> => {
  const rs = await clientApi.put<TUpdatePartnerResDto>(`/partners/${id}`, payload);
  return rs.data;
};

export const fetchPartnerInfoApi = async (id: string | number): Promise<TGetPartnerResDto> => {
  const { data } = await clientApi.get<TGetPartnerResDto>(`/partners/${id}`);
  return data;
};

export const usePartnerInfo = (id?: number) => {
  const { partnerId } = useParams();

  const fetchId = id ?? partnerId;
  return useQuery({
    queryKey: [QUERY_KEYS.PARTNER.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchPartnerInfoApi(fetchId) : undefined;
    },
    enabled: Boolean(fetchId),
  });
};
